import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import assessmentContextSidebarHelp from "@static/img/assessment-context-sidebar-help.png";
import { Editor } from "@tiptap/core";
import { compact, uniqBy } from "lodash";
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  AssessmentSuggestionCitationFragment,
  AssessmentSuggestionFragment,
  GetAssessmentAnswerSuggestedAnswersQuery,
  GetAssessmentAnswerSuggestedAnswersQueryVariables,
  GetAssessmentSuggestedAnswersQuery,
  GetAssessmentSuggestedAnswersQueryVariables,
  RegenerateAssessmentAnswersMutation,
  RegenerateAssessmentAnswersMutationVariables,
} from "types/graphql-schema";

import getAssessmentAnswerSuggestedAnswersQuery from "@apps/assessments/graphql/get-assessment-answer-suggested-answers-query";
import getAssessmentSuggestedAnswersQuery from "@apps/assessments/graphql/get-assessment-suggested-answers-query";
import regenerateAssessmentAnswersMutation from "@apps/assessments/graphql/regenerate-assessment-answers-mutation";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, isAdminVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Callout from "@components/callout/callout";
import GraphqlError from "@components/error/graphql-error";
import Layout from "@components/layout/layout";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { getAssetUrl } from "@helpers/helpers";

import { assessmentContextSidebarVar } from "./context-sidebar-context";
import AssessmentContextSidebarSource from "./source";

const lowSourceCount = 3;

const AssessmentContextSidebar = () => {
  const label = useLabel();
  const isAdmin = isAdminVar();
  const currentOrganization = currentOrganizationVar();
  const [expandedResponses, setExpandedResponses] = useState(true);
  const [expandedSources, setExpandedSources] = useState(true);
  const assessmentContextSidebar = useReactiveVar(assessmentContextSidebarVar);
  const params = useParams<{ assessmentId?: string }>();
  const assessmentId = params.assessmentId
    ? parseInt(params.assessmentId)
    : null;

  const fetchAssessment = !!assessmentId;
  const {
    error: getAssessmentSuggestedAnswersError,
    data: assessmentData,
    loading: getAssessmentSuggestedAnswersLoading,
  } = useQuery<
    GetAssessmentSuggestedAnswersQuery,
    GetAssessmentSuggestedAnswersQueryVariables
  >(getAssessmentSuggestedAnswersQuery, {
    variables: { assessmentId: assessmentId || 0 },
    skip: !fetchAssessment,
    context: { skipGraphqlErrorHandling: true },
  });

  const {
    error: getAssessmentAnswerSuggestedAnswersError,
    data: assessmentAnswerData,
    loading: getAssessmentAnswerSuggestedAnswersLoading,
  } = useQuery<
    GetAssessmentAnswerSuggestedAnswersQuery,
    GetAssessmentAnswerSuggestedAnswersQueryVariables
  >(getAssessmentAnswerSuggestedAnswersQuery, {
    variables: { assessmentAnswerId: assessmentContextSidebar?.answerId || 0 },
    skip: !assessmentContextSidebar?.answerId || fetchAssessment,
    context: { skipGraphqlErrorHandling: true },
  });

  const [
    regenerateQuestionAnswers,
    { loading: regenerateQuestionAnswersLoading },
  ] = useMutation<
    RegenerateAssessmentAnswersMutation,
    RegenerateAssessmentAnswersMutationVariables
  >(regenerateAssessmentAnswersMutation);
  const loading =
    getAssessmentSuggestedAnswersLoading ||
    getAssessmentAnswerSuggestedAnswersLoading ||
    regenerateQuestionAnswersLoading;

  // parse api data
  const assessment = assessmentData?.assessment;
  const assessmentAnswer = assessmentAnswerData?.assessmentAnswer;

  const suggestedAnswers = compact(
    assessment?.suggestedAnswers?.edges.map((edge) => edge?.node),
  );
  const matchingQuestion = suggestedAnswers.find(
    (question) =>
      String(question.questionId) ===
      String(assessmentContextSidebar?.questionId),
  );
  const suggestions = compact(
    matchingQuestion?.suggestions?.edges.map((edge) => edge?.node) || [],
  ) as AssessmentSuggestionFragment[];

  const sources =
    // extract from assessmentAnswer if it exists
    assessmentAnswer &&
    assessmentAnswer?.__typename === "TextAssessmentAnswerNode"
      ? compact(
          assessmentAnswer.inferredSources?.edges.map((edge) => edge?.node),
        )
      : assessment
        ? // extract from assessment if it exists
          (uniqBy(
            suggestions.flatMap(
              (suggestion) =>
                suggestion.citations?.edges.map((edge) => edge?.node) || [],
            ),
            "sourceId",
          ) as AssessmentSuggestionCitationFragment[])
        : [];

  const handleRefreshResponses = () => {
    if (!assessmentId || !assessmentContextSidebar?.questionId) return;
    regenerateQuestionAnswers({
      variables: {
        assessmentId: assessmentId,
        questionId: assessmentContextSidebar.questionId,
      },
    });
  };

  const handleAddResponse = (response: string) => {
    if (!assessmentContextSidebar) return;
    const element = document.querySelector(
      `#assessment-question-${assessmentContextSidebar.questionId} .ProseMirror`,
    ) as HTMLDivElement & { editor: Editor };
    if (element && element.editor) {
      // insert content at the end of the editor and focus on it
      const contentToInsert = element.editor.isEmpty
        ? response
        : `\n\n${response}`;
      element.editor
        .chain()
        .focus("end")
        .insertContent(contentToInsert)
        .focus()
        .run();
    }
  };

  const handleClickContactUs = () => {
    if (window.Intercom) {
      window.Intercom("showNewMessage");
    }
  };

  return assessmentContextSidebar?.questionId ? (
    <div>
      <Layout.SidebarSection className="border-none pb-0">
        {loading ? (
          <div className="text-base font-semibold text-gray-800 h-6 skeleton rounded-md" />
        ) : (
          <div className="text-base font-semibold text-gray-800">
            {assessmentAnswer?.question?.title ||
              matchingQuestion?.questionText ||
              "Question"}
          </div>
        )}
      </Layout.SidebarSection>
      {(getAssessmentAnswerSuggestedAnswersError ||
        getAssessmentSuggestedAnswersError) && (
        <Layout.SidebarSection className="border-none">
          <GraphqlError
            error={
              getAssessmentAnswerSuggestedAnswersError ||
              getAssessmentSuggestedAnswersError
            }
          />
        </Layout.SidebarSection>
      )}
      {!loading &&
        sources.length > 0 &&
        sources.length <= lowSourceCount &&
        assessmentContextSidebar.showSuggestedResponses && (
          <Layout.SidebarSection className="border-none">
            <Callout
              icon="warning"
              theme="warning"
              center
              title="Low source count"
            >
              We weren't able to find many sources for this question. Generated
              responses are based on a low source count.
            </Callout>
          </Layout.SidebarSection>
        )}
      {(loading || sources.length > 0) &&
        assessmentContextSidebar.showSuggestedResponses && (
          <Layout.SidebarSection
            className="border-none"
            title="Responses to Get Started With"
            showExpansionControls
            isExpanded={expandedResponses}
            onToggleExpand={setExpandedResponses}
            rightSide={
              <Button
                icon={getTopicflowIcon("refresh")}
                theme={buttonTheme.iconGray}
                size={ButtonSize.small}
                disabled={loading}
                onClick={handleRefreshResponses}
              />
            }
          >
            {loading ? (
              <div className="flex flex-col gap-3">
                <div className="h-16 skeleton rounded-md" />
                <div className="h-16 skeleton rounded-md" />
                <div className="h-16 skeleton rounded-md" />
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {suggestions.map((suggestion) => (
                  <div
                    key={suggestion.text}
                    className="rounded-md border py-3 px-4"
                  >
                    <div className="mb-2 flex justify-between">
                      <div className="flex-1 tf-header-sm">
                        {suggestion.title}
                      </div>
                      {suggestion.text && (
                        <Button
                          icon={getTopicflowIcon("plus")}
                          theme={buttonTheme.iconGray}
                          size={ButtonSize.small}
                          onClick={() => handleAddResponse(suggestion.text)}
                        />
                      )}
                    </div>
                    <div className="tf-text-sm">{suggestion.text}</div>
                  </div>
                ))}
              </div>
            )}
          </Layout.SidebarSection>
        )}
      <Layout.SidebarSection
        className="border-none"
        title={`All Relevant Sources`}
        showExpansionControls
        isExpanded={expandedSources}
        onToggleExpand={setExpandedSources}
      >
        {loading ? (
          <div className="flex flex-col gap-3">
            <div className="h-6 skeleton rounded-md" />
            <div className="h-6 skeleton rounded-md" />
            <div className="h-6 skeleton rounded-md" />
            <div className="h-6 skeleton rounded-md" />
            <div className="h-6 skeleton rounded-md" />
            <div className="h-6 skeleton rounded-md" />
          </div>
        ) : sources.length > 0 ? (
          <div className="flex flex-col gap-3">
            {sources.map((source) => (
              <AssessmentContextSidebarSource
                key={source.sourceId}
                source={source}
              />
            ))}
          </div>
        ) : (
          <Callout
            icon="fileSearch"
            center
            title="No Relevant Sources Found for this Question"
          >
            We couldn't find any {label("goal", { pluralize: true })},{" "}
            {label("feedback", { pluralize: true })}, meeting notes, or
            connected tool data related to this question. To see more context in
            future {label("review", { pluralize: true })}, try integrating the
            tools where this work happens.
            <br />
            <br />
            You can also contact us to import data for this {label(
              "review",
            )}{" "}
            cycle.
            <div className="mt-4 flex justify-center gap-4">
              {window.Intercom ? (
                <Button
                  text="Contact Us"
                  size={ButtonSize.medium}
                  onClick={handleClickContactUs}
                />
              ) : (
                <Button
                  text="Contact Us"
                  size={ButtonSize.medium}
                  url="mailto:support@topicflow.com"
                />
              )}
              {isAdmin && (
                <Button
                  text="Connect Tools"
                  theme={buttonTheme.primary}
                  size={ButtonSize.medium}
                  to={`settings/organization/${currentOrganization.id}/integrations`}
                />
              )}
            </div>
          </Callout>
        )}
      </Layout.SidebarSection>
    </div>
  ) : (
    <Layout.SidebarSection>
      <Callout
        icon="ai"
        theme="default"
        center
        title={`Generate Context for your ${label("review", {
          capitalize: true,
        })} Questions`}
      >
        Topicflow can generate context for any {label("review")} question.
        Context lets you get started with potential responses and view relevant
        sources for evidence-based answers.
        <br />
        <br />
        Click the Context button for any comment or text-based question to get
        started.
        <img
          src={getAssetUrl(assessmentContextSidebarHelp)}
          alt="Context sidebar"
          className="w-full border rounded-md"
        />
      </Callout>
    </Layout.SidebarSection>
  );
};

export default AssessmentContextSidebar;
